import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { FiArrowRight, FiX } from "react-icons/fi";
import { getGrowthRewards } from "../api/hrmApi";
import "./GrowthReminder.css";

export function isOtherGrowthView(user, viewUserId) {
  if (!viewUserId) return false;
  return ![user?.id, user?.contactId, user?.employeeSalesforceId, user?.email]
    .filter(value => value !== undefined && value !== null && value !== "")
    .some(value => String(value) === String(viewUserId));
}

export function useGrowthReminder(employeeKey) {
  const [state, setState] = useState({});
  const [dismissed, setDismissed] = useState([]);
  useEffect(() => {
    if (!employeeKey) return undefined;
    let controller, retryTimer;
    const load = (attempt = 0) => {
      clearTimeout(retryTimer);
      controller?.abort();
      controller = new AbortController();
      const request = controller;
      Promise.resolve().then(() => getGrowthRewards("", "", { signal: request.signal })).then(payload => {
        if (!request.signal.aborted) setState({ employeeKey, data: payload?.data || payload });
      }).catch(error => {
        if (request.signal.aborted) return;
        setState({ employeeKey });
        if (attempt === 0 && error?.status !== 401) retryTimer = setTimeout(() => load(1), 5000);
      });
    };
    const refresh = () => load();
    const visible = () => { if (document.visibilityState === "visible") load(); };
    const restored = event => { if (event.persisted) { setDismissed([]); load(); } };
    setDismissed([]);
    load();
    document.addEventListener("visibilitychange", visible);
    window.addEventListener("pageshow", restored);
    window.addEventListener("hrm-growth-saved", refresh);
    window.addEventListener("online", refresh);
    return () => {
      controller?.abort();
      clearTimeout(retryTimer);
      document.removeEventListener("visibilitychange", visible);
      window.removeEventListener("pageshow", restored);
      window.removeEventListener("hrm-growth-saved", refresh);
      window.removeEventListener("online", refresh);
    };
  }, [employeeKey]);
  const data = state.employeeKey === employeeKey ? state.data : null;
  const eligible = data?.available === true && data?.canSubmit === true;
  const periods = eligible && Array.isArray(data.pendingPeriods) ? data.pendingPeriods : [];
  const period = periods.find(item => !dismissed.includes(`${employeeKey}:${item.month}:${item.half}`));
  return {
    isNew: eligible && data.launchNew === true,
    period,
    dismiss: () => setDismissed(periods.map(item => `${employeeKey}:${item.month}:${item.half}`)),
  };
}

export default function GrowthReminder({ period, blocked, onDismiss, onSubmit }) {
  const keepFocus = event => {
    if (event.key !== "Tab") return;
    const buttons = event.currentTarget.querySelectorAll("button:not(:disabled)");
    const first = buttons[0], last = buttons[buttons.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };
  const month = period ? new Date(`${period.month}-01T00:00:00Z`).toLocaleDateString("en-IN", { month: "long", year: "numeric", timeZone: "Asia/Kolkata" }) : "";
  const deadline = period ? new Date(new Date(period.closesAt).getTime() - 1).toLocaleDateString("en-IN", { day: "numeric", month: "long", timeZone: "Asia/Kolkata" }) : "";
  return <Modal show={!!period && !blocked} onHide={onDismiss} onKeyDown={keepFocus} centered animation={false} backdrop="static" backdropClassName="new-hrm-growth-reminder-backdrop" className="new-hrm-growth-reminder" aria-labelledby="growth-reminder-title" aria-describedby="growth-reminder-description">
    <div className="hrm-growth-reminder-heading"><span>Growth rewards</span><button type="button" title="Close reminder" aria-label="Close reminder" onClick={onDismiss}><FiX /></button></div>
    <h2 id="growth-reminder-title">Your impact is still pending</h2>
    <p className="hrm-growth-reminder-period">{month} / {period?.half === 1 ? "First" : "Second"} half</p>
    <p id="growth-reminder-description">Share the work you made a difference with, what changed, and your self-review. Proof is optional.</p>
    <div className="hrm-growth-reminder-deadline"><strong>Submit by {deadline}, 11:59 pm IST</strong><p>Not submitting may result in 0 for this period's calculation.</p></div>
    <div className="hrm-growth-reminder-actions"><button type="button" className="hrm-growth-reminder-submit" onClick={() => onSubmit(period)}>Submit now <FiArrowRight aria-hidden="true" /></button><button type="button" onClick={onDismiss}>Later</button></div>
  </Modal>;
}
